import './App.css';
import moment from "moment-timezone";
import { toast } from "react-toastify"
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import MainLayoutV2 from './layout';
import { SignIn, ForgotCredential } from './Signin';
import { Users, User, UserProfile } from './model/User';
import { TeamDataTree } from './model/Team';
import { Accounts } from './model/Account';
import { VerifyEmail } from './Signup';
import { Home } from './component/Home';
import { Settings, UserSettings } from './model/Setting';
import Dashboard from './component/Dashboard';
import { MyVideos } from './model/Video';
import { UserTutorial } from './component/Tutorial';
import { Case } from './model/Case';
import { MyPreloaded } from './Upload';
import { MFAVerify } from './MFA';
import { Reports } from './model/Report';
import { SSOSignIn, SSOVerify } from './SSO';
import { Tasks } from './model/Task';
import { Invoices } from './model/Invoice';
import { Archives } from './model/Archive';
import { Devices } from './model/Device';
import { Announcement } from './component/Announcement';
import useAuth from './hook/useAuth';
import { useIdleLogout, IdleWarning } from './hook/useIdleLogout';

export const enableStripe = `${process.env.REACT_APP_ENABLE_STRIPE}` === "true"
export const enforceRetention = true

export default function App() {

  const { showWarning, secondsLeft, staySignedIn, signOutNow } = useIdleLogout(); // 10 min + 1 min warning

  return (
    <>
    <Routes>
      <Route element={<MainLayoutV2 />}>
        <Route path="/" element={<Dashboard />} />
        <Route path='/disclaimer' element={<Home/>} />
        <Route path='/privacy' element={<Home/>} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signin/mfa" element={<MFAVerify />} />
        <Route path="/signin/forgot" element={<ForgotCredential />} />
        <Route path="/sso" element={<SSOSignIn />} />
        <Route path="/sso/verify" element={<SSOVerify />} />
        {/* <Route path="/signup" element={<SignUp />} /> */}
        <Route path="/verifyemail" element={<VerifyEmail />} />
        <Route path="/dashboard/" element={<ProtectedRoute element={<Dashboard />} />} />
        <Route path="/team/" element={<ProtectedRoute element={<TeamDataTree />} />} />
        <Route path="/team/admin" element={<RootOnlyRoute element={<TeamDataTree />} />} />
        <Route path="/team/:id" element={<ProtectedRoute element={<TeamDataTree />} />} />
        <Route path="/user/" element={<RootOnlyRoute element={<Users />} />} />
        <Route path="/user/:id" element={<ProtectedRoute element={<User />} />} />
        <Route path="/setting" element={<RootOnlyRoute element={<Settings />} />} />
        <Route path="/profile" element={<ProtectedRoute element={<UserProfile />} />} />
        <Route path="/setting/user" element={<ProtectedRoute element={<UserSettings />} />} />
        <Route path="/case/:id" element={<ProtectedRoute element={<Case />} />} />
        <Route path="/video/" element={<ProtectedRoute element={<MyVideos />} />} />
        {/* <Route path="/video/drafted" element={<ProtectedRoute element={<MyDrafted />} />} /> */}
        <Route path="/report/" element={<RootOnlyRoute element={<Reports />} />} />            
        {/* <Route path="/report/:id" element={<ProtectedRoute element={<ReviewVideoByCase />} />} /> */}
        <Route path="/tutorial" element={<ProtectedRoute element={<UserTutorial />} />} />
        <Route path="/upload" element={<ProtectedRoute element={<MyPreloaded />} />} />
        <Route path="/task/" element={<ProtectedRoute element={<Tasks />} />} />
        <Route path="/archive/" element={<ProtectedRoute element={<Archives />} />} />
        <Route path="/device" element={<RootOnlyRoute element={<Devices />} />} />
        <Route path="/announcement" element={<RootOnlyRoute element={<Announcement />} />} />

        {/* { enableStripe && <Route path="/billing" element={<RootOnlyRoute element={<Billings />} />} />}
        { enableStripe && <Route path="/plan" element={<RootOnlyRoute element={<Plans />} />} />}
        { enableStripe && <Route path="/subscribe" element={<RootOnlyRoute element={<Subscribe />} />} />}
        { enableStripe && <Route path="/subscription" element={<RootOnlyRoute element={<MySubscriptions />} />} />} */}

        <Route path="/account/" element={<DeepvinOnlyRoute element={<Accounts />} />} />
        <Route path="/account/:uuid/team" element={<DeepvinOnlyRoute element={<TeamDataTree />} />} />
        <Route path="/account/:uuid/device" element={<DeepvinOnlyRoute element={<Devices />} />} />
        <Route path="/invoice/" element={<DeepvinOnlyRoute element={<Invoices />} />} />
      </Route>
    </Routes>
    <IdleWarning
        open={showWarning}
        secondsLeft={secondsLeft}
        onStay={staySignedIn}
        onSignOut={signOutNow}
      />
    </>
  )
}

export const ProtectedRoute = ({ element }) => {
  const { auth } = useAuth()
  const location = useLocation()
  return Object.keys(auth).length > 0 
  ? (element || <Outlet />) 
  : (<Navigate to="/signin" state={{ from: location }} replace />)
}

export const RootOnlyRoute = ({ element }) => {
  const { auth } = useAuth()
  const location = useLocation()
  return auth && auth.IsRoot 
  ? (element || <Outlet />) 
  : (<Navigate to="/signin" state={{ from: location }} replace />)
}

export const DeepvinOnlyRoute = ({ element }) => {
  const { auth } = useAuth()
  const location = useLocation()
  return auth && auth.IsRoot && auth.AccountID === 1
  ? (element || <Outlet />) 
  : (<Navigate to="/signin" state={{ from: location }} replace />)
}

export const enforceRetentionAlert = (userWithSettings) => {

  // console.log("userWithSettings: ", userWithSettings)
  if (userWithSettings?.IsRoot && userWithSettings?.Account?.AccountType === "Customer"){
    const fixedDate = "2025-06-16"
    const currentDate = moment() // Get the current date and time
    const fixedMoment = moment.tz(fixedDate, "YYYY-MM-DD", "America/New_York")// Parse fixed date in Eastern timezone

    if (currentDate.isBefore(fixedMoment)) {
      toast.warning("Starting on June 16 2025, DeepVin will enforce Retention period for videos!" 
        + "\n\nPlease make sure Retention Days is set properly in Account Policy under Admin Settings."
        + "\nOnce Retention period passes, the videos will be removed from cloud storage!", {autoClose:false})
    }    
  }

}

export const newUETransferAppAlert = (userWithSettings) => {

  if (userWithSettings?.IsRoot && userWithSettings?.Account?.AccountType === "Customer"){
    const fixedDate = "2026-07-24"
    const currentDate = moment() // Get the current date and time
    const fixedMoment = moment.tz(fixedDate, "YYYY-MM-DD", "America/New_York")// Parse fixed date in Eastern timezone

    if (currentDate.isBefore(fixedMoment)) {
      toast.warning(
        <div style={{ whiteSpace: "pre-line" }}>
          {"UE Transfer App 2.0 is now available in Apple App store!"
          + "\n\nThis new version includes major UI overhauls and underlying process improvements! The download and upload performance has been significantly enhanced, especially for files uploading."}
        </div>, {autoClose:false})
    }    
  }

}